import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import SpeedIcon from '@mui/icons-material/Speed';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import { api } from '../../services/api';
import { resolveIperfTestChannelBand } from '../../utils/iperfChannelUtils';

const STATUS_META = {
  queued:    { label: 'Queued',    color: 'info'    },
  running:   { label: 'Running',   color: 'warning' },
  completed: { label: 'Completed', color: 'success' },
  failed:    { label: 'Failed',    color: 'error'   },
  cancelled: { label: 'Cancelled', color: 'default' },
  pending:   { label: 'Pending',   color: 'default' },
  timed_out: { label: 'Timed Out', color: 'error'   },
};

const isTerminal = (status) => ['completed', 'failed', 'cancelled'].includes(status);

const fmt = (v) => (typeof v === 'number' ? v.toFixed(1) : '—');
const fmtSigned = (v) => (typeof v === 'number' ? `${v > 0 ? '+' : ''}${v.toFixed(1)}` : '—');
const DIFF_THRESHOLD_PCT = 10;
const LOSS_THRESHOLD_PP = 1;

const findRawPerf = (raw) => raw?.perf ?? raw?.status?.perf ?? null;

const resolveThroughput = (t) => {
  if (typeof t?.throughput_mbps === 'number' && t.throughput_mbps >= 0) {
    return t.throughput_mbps;
  }
  const perf = findRawPerf(t?.raw_result);
  if (typeof perf?.bw !== 'number') return null;
  const unit = String(perf.bw_unit || '').toLowerCase();
  if (unit.includes('gbits')) return perf.bw * 1000;
  if (unit.includes('kbits')) return perf.bw / 1000;
  return perf.bw;
};

const resolveLoss = (t) => {
  if (typeof t.packet_loss_pct === 'number' && t.packet_loss_pct >= 0) return t.packet_loss_pct;
  const dl = findRawPerf(t.raw_result)?.data_loss;
  if (dl == null) return null;
  const parsed = parseFloat(String(dl).replace('%', ''));
  return isNaN(parsed) ? null : parsed;
};

const resolveThroughputMbps = (t) => resolveThroughput(t);

const getDominant = (countsMap) => {
  if (!countsMap || typeof countsMap !== 'object') return null;
  const entries = Object.entries(countsMap);
  if (entries.length === 0) return null;
  let maxKey = entries[0][0];
  let maxVal = entries[0][1];
  for (let i = 1; i < entries.length; i++) {
    if (entries[i][1] > maxVal) {
      maxVal = entries[i][1];
      maxKey = entries[i][0];
    }
  }
  return maxKey;
};

const resolveRfStats = (t) => {
  const perf = findRawPerf(t?.raw_result);
  if (!perf) return { quality: null, bw: null, mcs: null, nss: null };
  const stats = perf.stats;
  return {
    quality: typeof perf.Quality === 'number' ? perf.Quality : (typeof perf.quality === 'number' ? perf.quality : null),
    bw: getDominant(stats?.bw_counts),
    mcs: getDominant(stats?.mcs_counts),
    nss: getDominant(stats?.nss_counts),
  };
};

const truncMcs = (mcs) => {
  if (!mcs) return '—';
  const match = mcs.match(/^(HE )?MCS (\d+)/);
  return match ? `MCS ${match[2]}` : mcs;
};

const computeDisplayedPathAnalysis = (path, tests) => {
  const e2e = tests.find(t =>
    t.type === 'end_to_end' &&
    t.from_serial?.toLowerCase() === path.leaf_serial?.toLowerCase() &&
    t.to_serial?.toLowerCase() === path.cap_serial?.toLowerCase()
  );
  const e2eMbps = resolveThroughputMbps(e2e) ?? path.e2e_throughput_mbps;

  const hopByChild = new Map();
  tests.filter(t => t.type === 'hop').forEach(t => {
    if (t.from_serial) hopByChild.set(t.from_serial.toLowerCase(), t);
  });

  const hopThroughputs = [];
  const hopChain = [];
  let cur = path.leaf_serial?.toLowerCase();
  const cap = path.cap_serial?.toLowerCase();
  const visited = new Set();
  while (cur && cur !== cap && !visited.has(cur)) {
    visited.add(cur);
    const hop = hopByChild.get(cur);
    if (!hop) break;
    hopChain.push(hop);
    const mbps = resolveThroughputMbps(hop);
    if (mbps != null) hopThroughputs.push(mbps);
    cur = hop.to_serial?.toLowerCase();
  }

  const bottleneck = hopThroughputs.length
    ? Math.min(...hopThroughputs)
    : (path.bottleneck_hop_mbps || null);
  const hops = path.hops || e2e?.path_hops || hopThroughputs.length;
  const expected = bottleneck != null
    ? bottleneck * Math.pow(0.95, hops)
    : path.expected_e2e_mbps;
  const rawDegradation = expected > 0 && e2eMbps != null
    ? ((expected - e2eMbps) / expected) * 100
    : path.degradation_pct;
  // Clamp to 0 — negative means E2E exceeded expected, which is healthy
  const degradation = typeof rawDegradation === 'number'
    ? Math.max(0, rawDegradation)
    : rawDegradation;

  return {
    ...path,
    e2e_throughput_mbps: e2eMbps,
    bottleneck_hop_mbps: bottleneck,
    expected_e2e_mbps: expected,
    degradation_pct: degradation,
    healthy: typeof degradation === 'number' ? degradation <= 10 : null,
    hopChain,
    e2eTest: e2e,
  };
};

// Build merged path data from an E2E test, overlaying analysis when available.
const buildPathForE2ETest = (e2eTest, analysisPaths, tests) => {
  const matchingPath = analysisPaths?.find(p =>
    p.leaf_serial?.toLowerCase() === e2eTest.from_serial?.toLowerCase() &&
    p.cap_serial?.toLowerCase() === e2eTest.to_serial?.toLowerCase()
  );
  const basePath = matchingPath || {
    leaf_serial: e2eTest.from_serial,
    cap_serial: e2eTest.to_serial,
    hops: e2eTest.path_hops,
    e2e_throughput_mbps: null,
    bottleneck_hop_mbps: null,
    expected_e2e_mbps: null,
    degradation_pct: null,
    healthy: null,
    flagged_hop: null,
  };
  return computeDisplayedPathAnalysis(basePath, tests);
};

const relativeTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

const RF_STATS_TOOLTIP = [
  'Q  — Link quality score (0–100)',
  'BW — Dominant channel width used during the test (e.g. 80MHZ)',
  'MCS — Most-used modulation & coding scheme (higher = faster)',
  'NSS — Spatial streams used (nss-4 = 4×4 MIMO)',
].join('\n');

const RfStatsCell = ({ test }) => {
  const rf = resolveRfStats(test);
  if (rf.quality == null && !rf.bw && !rf.mcs && !rf.nss) return <TableCell align="right">—</TableCell>;
  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{RF_STATS_TOOLTIP}</span>} placement="left">
      <TableCell align="right" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap', cursor: 'help' }}>
        {rf.quality != null && <span>Q:{rf.quality} </span>}
        {rf.bw && <span>BW:{rf.bw} </span>}
        {rf.mcs && <span>{truncMcs(rf.mcs)} </span>}
        {rf.nss && <span>{rf.nss}</span>}
      </TableCell>
    </Tooltip>
  );
};

// Build a lookup of serial → device from the devices prop.
const buildDeviceLookup = (devices) => {
  const m = new Map();
  (devices || []).forEach((d) => {
    if (d?.serial_number) m.set(d.serial_number.toLowerCase(), d);
  });
  return m;
};

// Return the uplink channel/band label for the child device (from_serial).
// ul_band: "5GHz" | "2.4GHz" | "6GHz"; ul_channel: integer channel number.
const resolveChannelBand = (test, deviceLookup, reverseDirection = false) =>
  resolveIperfTestChannelBand(test, deviceLookup, { reverseDirection });

const compareKeyForTest = (t) => {
  if (!t?.type || !t.from_serial || !t.to_serial) return null;
  return `${t.type}:${t.from_serial.toLowerCase()}:${t.to_serial.toLowerCase()}`;
};

const comparePathLabel = (t) =>
  `${t?.from_serial?.slice(-6) || '—'} → ${t?.to_serial?.slice(-6) || '—'}`;

const compareTypeLabel = (type) => {
  if (type === 'end_to_end') return 'E2E';
  if (type === 'all_to_cap') return 'To CAP';
  return 'Hop';
};

const getIperfCoverageLabels = (reverse) => ({
  hop: {
    title: reverse ? 'Parent → child' : 'Child → parent',
    subtitle: reverse
      ? 'One test per link, traffic from parent toward each child'
      : 'One test per link, every device toward its parent',
  },
  e2e: {
    title: reverse ? 'CAP → leaf' : 'Leaf → CAP',
    subtitle: reverse
      ? 'Deepest leaf in each subtree, traffic from CAP'
      : 'Deepest leaf in each subtree, end-to-end toward CAP',
  },
  allToCap: {
    title: reverse ? 'CAP → every device' : 'Every device → CAP',
    subtitle: reverse
      ? 'CAP sends to each managed device in the tree'
      : 'Each managed device sends directly to the CAP',
  },
});

const buildComparableTests = (run) => {
  const out = new Map();
  (run?.tests || []).forEach((t) => {
    if (t?.status !== 'completed') return;
    if (t.type !== 'hop' && t.type !== 'end_to_end' && t.type !== 'all_to_cap') return;
    const key = compareKeyForTest(t);
    if (!key) return;
    out.set(key, t);
  });
  return out;
};

const relativeDeltaPct = (before, after) => {
  if (typeof before !== 'number' || typeof after !== 'number' || before <= 0) return null;
  return ((after - before) / before) * 100;
};

const buildIperfComparison = (beforeRun, afterRun) => {
  if (!beforeRun || !afterRun) return null;
  const before = buildComparableTests(beforeRun);
  const after = buildComparableTests(afterRun);
  const rows = [];
  let compared = 0;
  let throughputImproved = 0;
  let throughputDegraded = 0;
  let lossImproved = 0;
  let lossDegraded = 0;

  before.forEach((beforeTest, key) => {
    const afterTest = after.get(key);
    if (!afterTest) return;
    compared += 1;

    const beforeMbps = resolveThroughput(beforeTest);
    const afterMbps = resolveThroughput(afterTest);
    const throughputPct = relativeDeltaPct(beforeMbps, afterMbps);
    const throughputDelta = (typeof beforeMbps === 'number' && typeof afterMbps === 'number')
      ? afterMbps - beforeMbps
      : null;
    const throughputZeroBaseline = beforeMbps === 0 && typeof afterMbps === 'number' && afterMbps > 0;
    const throughputSignificant = throughputZeroBaseline ||
      (throughputPct != null && Math.abs(throughputPct) >= DIFF_THRESHOLD_PCT);

    const beforeLoss = resolveLoss(beforeTest);
    const afterLoss = resolveLoss(afterTest);
    const lossDeltaPP = (typeof beforeLoss === 'number' && typeof afterLoss === 'number')
      ? afterLoss - beforeLoss
      : null;
    const lossPct = relativeDeltaPct(beforeLoss, afterLoss);
    const lossSignificant = typeof lossDeltaPP === 'number' && (
      Math.abs(lossDeltaPP) >= LOSS_THRESHOLD_PP ||
      (lossPct != null && Math.abs(lossPct) >= DIFF_THRESHOLD_PCT)
    );

    if (!throughputSignificant && !lossSignificant) return;
    if (throughputSignificant) {
      if (throughputDelta > 0) throughputImproved += 1;
      if (throughputDelta < 0) throughputDegraded += 1;
    }
    if (lossSignificant) {
      if (lossDeltaPP < 0) lossImproved += 1;
      if (lossDeltaPP > 0) lossDegraded += 1;
    }

    const good = (throughputSignificant && throughputDelta > 0) || (lossSignificant && lossDeltaPP < 0);
    const bad = (throughputSignificant && throughputDelta < 0) || (lossSignificant && lossDeltaPP > 0);
    rows.push({
      key,
      type: beforeTest.type,
      path: comparePathLabel(beforeTest),
      beforeMbps,
      afterMbps,
      throughputDelta,
      throughputPct,
      throughputSignificant,
      beforeLoss,
      afterLoss,
      lossDeltaPP,
      lossPct,
      lossSignificant,
      verdict: good && bad ? 'mixed' : (bad ? 'worse' : 'better'),
    });
  });

  rows.sort((a, b) => {
    const aScore = Math.max(
      a.beforeMbps === 0 && a.afterMbps > 0 ? 100 : Math.abs(a.throughputPct || 0),
      Math.abs(a.lossPct || 0),
      Math.abs(a.lossDeltaPP || 0) * 10
    );
    const bScore = Math.max(
      b.beforeMbps === 0 && b.afterMbps > 0 ? 100 : Math.abs(b.throughputPct || 0),
      Math.abs(b.lossPct || 0),
      Math.abs(b.lossDeltaPP || 0) * 10
    );
    return bScore - aScore;
  });

  return {
    beforeRun,
    afterRun,
    compared,
    unmatchedBefore: [...before.keys()].filter(k => !after.has(k)).length,
    unmatchedAfter: [...after.keys()].filter(k => !before.has(k)).length,
    rows,
    throughputImproved,
    throughputDegraded,
    lossImproved,
    lossDegraded,
  };
};

const capSerialForRun = (run) => {
  if (!run) return null;
  if (Array.isArray(run.cap_serials) && run.cap_serials.length === 1) {
    return run.cap_serials[0];
  }
  if (run.cap_serial && !String(run.cap_serial).includes('CAPs')) {
    return run.cap_serial;
  }
  return null;
};

const runCapLabel = (run, deviceLookup) => {
  if (!run) return '—';
  if (run.cap_serials?.length > 1) {
    return `${run.cap_serials.length} CAPs`;
  }
  const serial = capSerialForRun(run);
  if (!serial) return '—';
  const dev = deviceLookup?.get?.(serial.toLowerCase());
  return dev?.name || serial;
};

const runDisplayName = (run, deviceLookup) => {
  if (!run) return '—';
  const name = run.name?.trim();
  if (name) return name;
  const cap = runCapLabel(run, deviceLookup);
  return cap !== '—' ? `${cap} iperf test` : '—';
};

const runLabel = (run, deviceLookup) => {
  if (!run) return '—';
  return `${runDisplayName(run, deviceLookup)} · ${relativeTime(run.created_at)}`;
};

const buildIperfCapPayload = (selectedCapSerials, allCaps) => {
  const serials = (selectedCapSerials || []).filter(Boolean);
  if (serials.length === 0) return null;
  if (serials.length === 1) {
    return { cap_serial: serials[0] };
  }
  if (allCaps.length > 1 && serials.length === allCaps.length) {
    return { cap_serial: 'all' };
  }
  return { cap_serials: serials };
};

const defaultIperfRunName = (selectedCapSerials, allCaps) => {
  if (selectedCapSerials.length === 0) return '';
  if (allCaps.length > 1 && selectedCapSerials.length === allCaps.length) {
    return 'Site-wide iperf test';
  }
  if (selectedCapSerials.length > 1) {
    return `${selectedCapSerials.length} CAPs iperf test`;
  }
  const serial = selectedCapSerials[0];
  const capDevice = allCaps.find((d) => d.serial_number === serial);
  const label = capDevice?.name || serial;
  return `${label} iperf test`;
};

const resolveHopRootCap = (fromSerial, toSerial, deviceLookup, capSet) => {
  const to = toSerial?.toLowerCase();
  if (to && capSet.has(to)) return to;

  let cur = fromSerial?.toLowerCase();
  const visited = new Set();
  while (cur && !visited.has(cur)) {
    visited.add(cur);
    if (capSet.has(cur)) return cur;
    const parent = deviceLookup.get(cur)?.parent_serial?.toLowerCase();
    if (!parent) break;
    cur = parent;
  }
  return null;
};

const resolveCapSections = (detailRun, deviceLookup) => {
  const tests = detailRun?.tests || [];
  const reverse = Boolean(detailRun?.reverse_direction);
  const multiCaps = Array.isArray(detailRun?.cap_serials) && detailRun.cap_serials.length > 1
    ? detailRun.cap_serials
    : null;
  const singleCap = detailRun?.cap_serial && !String(detailRun.cap_serial).includes('CAPs')
    ? detailRun.cap_serial
    : null;

  const allToCapCapSerial = (t) => (reverse ? t.from_serial : t.to_serial)?.toLowerCase();

  if (!multiCaps) {
    const capSerial = singleCap || detailRun?.cap_serial || '—';
    return [{
      capSerial,
      reverse,
      hopTests: tests.filter((t) => t.type === 'hop'),
      e2eTests: tests.filter((t) => t.type === 'end_to_end'),
      allToCapTests: tests.filter((t) => t.type === 'all_to_cap'),
      analysisPaths: detailRun?.analysis?.paths || [],
    }];
  }

  const capSet = new Set(multiCaps.map((s) => s.toLowerCase()));
  const hopByCap = new Map(multiCaps.map((c) => [c.toLowerCase(), []]));
  const e2eByCap = new Map(multiCaps.map((c) => [c.toLowerCase(), []]));
  const allToCapByCap = new Map(multiCaps.map((c) => [c.toLowerCase(), []]));

  tests.forEach((t) => {
    if (t.type === 'end_to_end') {
      const cap = (reverse ? t.from_serial : t.to_serial)?.toLowerCase();
      if (cap && e2eByCap.has(cap)) e2eByCap.get(cap).push(t);
      return;
    }
    if (t.type === 'all_to_cap') {
      const cap = allToCapCapSerial(t);
      if (cap && allToCapByCap.has(cap)) allToCapByCap.get(cap).push(t);
      return;
    }
    if (t.type === 'hop') {
      const cap = resolveHopRootCap(t.from_serial, t.to_serial, deviceLookup, capSet);
      if (cap && hopByCap.has(cap)) hopByCap.get(cap).push(t);
    }
  });

  return multiCaps.map((cap) => {
    const key = cap.toLowerCase();
    return {
      capSerial: cap,
      reverse,
      hopTests: hopByCap.get(key) || [],
      e2eTests: e2eByCap.get(key) || [],
      allToCapTests: allToCapByCap.get(key) || [],
      analysisPaths: (detailRun?.analysis?.paths || []).filter(
        (p) => p.cap_serial?.toLowerCase() === key,
      ),
    };
  });
};

const pathExpandKey = (capSerial, index) => `${String(capSerial).toLowerCase()}:${index}`;

const EMPTY_DEVICES = [];

const IperfTestPanel = ({
  devices = [],
  isActive = true,
  initialCapSerials = null,
  initialFocusSerials = null,
  onCapScopeApplied,
}) => {
  const safeDevices = devices ?? EMPTY_DEVICES;
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startLoading, setStartLoading] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [singleRerunTestId, setSingleRerunTestId] = useState(null);
  const [error, setError] = useState(null);
  const [queuedInfo, setQueuedInfo] = useState(null);

  const [selectedCapSerials, setSelectedCapSerials] = useState([]);
  const [mapFocusSerials, setMapFocusSerials] = useState([]);
  const [testName, setTestName] = useState('');
  const [duration, setDuration] = useState(20);
  const [protocol, setProtocol] = useState('UDP');
  const [udpBandwidth, setUdpBandwidth] = useState(2000);
  const [includeHopTests, setIncludeHopTests] = useState(true);
  const [includeE2ETests, setIncludeE2ETests] = useState(true);
  const [includeAllToCapTests, setIncludeAllToCapTests] = useState(true);
  const [reverseDirection, setReverseDirection] = useState(false);
  const [showParams, setShowParams] = useState(false);
  const [showTimingParams, setShowTimingParams] = useState(false);

  const [selectedRun, setSelectedRun] = useState(null);
  const selectedRunRef = useRef(null);
  const [sseKey, setSseKey] = useState(0); // bump to force SSE reconnect on same run ID
  const [detailRun, setDetailRun] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [expandedPaths, setExpandedPaths] = useState(new Set());
  const [showHopTests, setShowHopTests] = useState(true);
  const [showAllToCapTests, setShowAllToCapTests] = useState(true);
  const [showPathAnalysis, setShowPathAnalysis] = useState(true);
  const [compareSelection, setCompareSelection] = useState([]);
  const compareSelectionRef = useRef([]);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareRuns, setCompareRuns] = useState([]);
  const [bulkSelection, setBulkSelection] = useState([]);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [compareMode, setCompareMode] = useState(false);

  const deviceLookup = useMemo(() => buildDeviceLookup(safeDevices), [safeDevices]);
  const comparison = useMemo(
    () => buildIperfComparison(compareRuns[0], compareRuns[1]),
    [compareRuns]
  );

  const caps = useMemo(() =>
    safeDevices.filter(d => {
      const model = d.capabilities?.model || '';
      return model.toLowerCase().includes('cap');
    }),
    [safeDevices]
  );

  const capDefaultAppliedRef = useRef(false);
  useEffect(() => {
    const fromMap = (initialCapSerials || []).filter(Boolean);
    const focusFromMap = (initialFocusSerials || []).filter(Boolean);
    if (fromMap.length === 0 && focusFromMap.length === 0) return;
    if (fromMap.length > 0 && caps.length > 0) {
      const capSet = new Set(caps.map((d) => d.serial_number));
      const matched = fromMap.filter((serial) => capSet.has(serial));
      setSelectedCapSerials(matched.length > 0 ? matched : fromMap);
      capDefaultAppliedRef.current = true;
    }
    if (focusFromMap.length > 0) {
      setMapFocusSerials(focusFromMap);
    }
    if (fromMap.length > 0 || focusFromMap.length > 0) {
      onCapScopeApplied?.();
    }
  }, [initialCapSerials, initialFocusSerials, caps, onCapScopeApplied]);

  useEffect(() => {
    if (capDefaultAppliedRef.current || caps.length === 0) return;
    if ((initialCapSerials || []).length > 0) return;
    setSelectedCapSerials([caps[0].serial_number]);
    capDefaultAppliedRef.current = true;
  }, [caps, initialCapSerials]);

  const handleCapSelectionChange = (event) => {
    const value = event.target.value;
    const next = typeof value === 'string' ? value.split(',') : value;
    if (next[next.length - 1] === '__all__') {
      setSelectedCapSerials(
        selectedCapSerials.length === caps.length
          ? []
          : caps.map((d) => d.serial_number),
      );
      return;
    }
    setSelectedCapSerials(next.filter((v) => v !== '__all__'));
  };

  const capSelectionLabel = useMemo(() => {
    if (selectedCapSerials.length === 0) return 'Select CAPs';
    if (caps.length > 1 && selectedCapSerials.length === caps.length) {
      return `All CAPs (${caps.length})`;
    }
    if (selectedCapSerials.length === 1) {
      return selectedCapSerials[0];
    }
    return `${selectedCapSerials.length} CAPs selected`;
  }, [selectedCapSerials, caps.length]);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listIperfTestRuns();
      setRuns(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Keep the ref in sync so SSE callbacks can always read the current selection.
  useEffect(() => { selectedRunRef.current = selectedRun; }, [selectedRun]);
  useEffect(() => { compareSelectionRef.current = compareSelection; }, [compareSelection]);

  // Runs can change status after retry/refresh. Drop non-completed selections so
  // the compare panel never compares stale or in-flight results.
  useEffect(() => {
    if (compareSelection.length === 0 || runs.length === 0) return;
    const completedIDs = new Set(runs.filter(r => r.status === 'completed').map(r => r.id));
    const next = compareSelection.filter(id => completedIDs.has(id));
    if (next.length !== compareSelection.length) {
      setCompareSelection(next);
      setCompareRuns([]);
    }
  }, [runs, compareSelection]);

  // Re-fetch runs (and refresh the selected run detail) whenever the tab becomes active.
  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    fetchRuns();
    if (selectedRunRef.current) {
      api.getIperfTestRun(selectedRunRef.current).then(data => {
        if (!cancelled && data?.id === selectedRunRef.current) setDetailRun(data);
      }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [isActive, fetchRuns]);

  // SSE subscription — only while the tab is visible.
  // `active` is set to false in cleanup so any callbacks that fire
  // asynchronously after abort (race) are silently ignored.
  useEffect(() => {
    if (!isActive || !selectedRun) return;

    let active = true;
    let pollHandle = null;

    const close = api.subscribeIperfTestRun(selectedRun, {
      onSnapshot: (data) => {
        if (!active || data?.id !== selectedRunRef.current) return;
        setDetailRun(data);
      },
      onTerminal: (data) => {
        if (!active || data?.id !== selectedRunRef.current) return;
        setDetailRun(data);
        fetchRuns();
      },
      onError: () => {
        if (!active) return;
        pollHandle = setInterval(async () => {
          if (!active) { clearInterval(pollHandle); return; }
          try {
            const data = await api.getIperfTestRun(selectedRunRef.current);
            if (!active || data?.id !== selectedRunRef.current) return;
            setDetailRun(data);
            if (isTerminal(data?.status)) {
              clearInterval(pollHandle);
              fetchRuns();
            }
          } catch (_) { /* ignore */ }
        }, 30000);
      },
    });

    return () => {
      active = false;
      close();
      if (pollHandle) clearInterval(pollHandle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, selectedRun, sseKey, fetchRuns]);

  const handleStart = async () => {
    const capPayload = buildIperfCapPayload(selectedCapSerials, caps);
    if (!capPayload) return;
    if (!includeHopTests && !includeE2ETests && !includeAllToCapTests) {
      setError('Select at least one test type before starting.');
      return;
    }
    setStartLoading(true);
    setError(null);
    setQueuedInfo(null);
    try {
      const payload = {
        ...capPayload,
        duration,
        protocol,
        udp_bandwidth: udpBandwidth,
        include_hop: includeHopTests,
        include_e2e: includeE2ETests,
        include_all_to_cap: includeAllToCapTests,
        reverse_direction: reverseDirection,
      };
      if (mapFocusSerials.length > 0) {
        payload.focus_serials = mapFocusSerials;
      }
      const trimmedName = testName.trim();
      if (trimmedName) {
        payload.name = trimmedName;
      } else {
        const autoName = defaultIperfRunName(selectedCapSerials, caps);
        if (autoName) payload.name = autoName;
      }
      const response = await api.createIperfTestRun(payload);
      const isQueued = response?.queued === true;
      const startedRuns = Array.isArray(response?.runs) ? response.runs : [response];
      await fetchRuns();
      const firstRun = startedRuns.find((r) => r?.id) || startedRuns[0];
      if (firstRun?.id) {
        selectedRunRef.current = firstRun.id;
        setSelectedRun(firstRun.id);
        setDetailRun(firstRun);
      }
      if (isQueued) {
        setQueuedInfo('Test queued — will start automatically when the current run finishes.');
      }
    } catch (e) {
      setError(e.message || 'Failed to start test');
    } finally {
      setStartLoading(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      await api.cancelIperfTestRun(id);
      await fetchRuns();
      if (selectedRun === id) {
        const data = await api.getIperfTestRun(id);
        if (data?.id === id) setDetailRun(data);
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const clearRunFromLocalState = useCallback((id) => {
    if (selectedRun === id) {
      setSelectedRun(null);
      setDetailRun(null);
    }
    setCompareSelection((prev) => prev.filter((runId) => runId !== id));
    setCompareRuns((prev) => prev.filter((run) => run?.id !== id));
    setBulkSelection((prev) => prev.filter((runId) => runId !== id));
    setRuns((prev) => prev.filter((run) => run.id !== id));
  }, [selectedRun]);

  const handleDelete = async (id) => {
    const tryDelete = () => api.deleteIperfTestRun(id);
    try {
      await tryDelete();
      clearRunFromLocalState(id);
      await fetchRuns();
    } catch (e) {
      const msg = e?.message || '';
      if (msg.toLowerCase().includes('cancel it first')) {
        try {
          await api.cancelIperfTestRun(id);
          await tryDelete();
          clearRunFromLocalState(id);
          await fetchRuns();
          return;
        } catch (retryErr) {
          setError(retryErr?.message || msg);
          return;
        }
      }
      setError(msg);
    }
  };

  const handleToggleRowSelect = useCallback((run) => {
    if (compareMode) {
      if (compareLoading || run.status !== 'completed') return;
      setCompareRuns([]);
      setCompareSelection((prev) => {
        if (prev.includes(run.id)) return prev.filter((runId) => runId !== run.id);
        if (prev.length >= 2) return prev;
        return [...prev, run.id];
      });
      return;
    }
    setBulkSelection((prev) => (
      prev.includes(run.id) ? prev.filter((runId) => runId !== run.id) : [...prev, run.id]
    ));
  }, [compareMode, compareLoading]);

  const handleToggleAllRows = useCallback(() => {
    if (compareMode) return;
    setBulkSelection((prev) => (
      prev.length === runs.length ? [] : runs.map((run) => run.id)
    ));
  }, [compareMode, runs]);

  const handleCompareModeToggle = useCallback(() => {
    setCompareMode((on) => {
      if (on) {
        setCompareSelection([]);
        setCompareRuns([]);
      } else {
        setBulkSelection([]);
      }
      return !on;
    });
  }, []);

  const handleDeleteBulk = useCallback(async (ids) => {
    const targets = (ids || []).filter(Boolean);
    if (targets.length === 0) return;
    if (!window.confirm(
      `Delete ${targets.length} test run${targets.length === 1 ? '' : 's'} from history?`,
    )) {
      return;
    }
    setDeleteBusy(true);
    setError(null);
    try {
      const deleteOne = async (id) => {
        try {
          await api.deleteIperfTestRun(id);
          return { id, ok: true };
        } catch (err) {
          const msg = err?.message || '';
          if (!msg.toLowerCase().includes('cancel it first')) {
            throw err;
          }
          await api.cancelIperfTestRun(id);
          await api.deleteIperfTestRun(id);
          return { id, ok: true };
        }
      };
      const results = await Promise.allSettled(targets.map((id) => deleteOne(id)));
      const failed = results.filter((r) => r.status === 'rejected');
      results.forEach((r, idx) => {
        if (r.status === 'fulfilled') clearRunFromLocalState(targets[idx]);
      });
      await fetchRuns();
      if (failed.length > 0) {
        const msg = failed[0].reason?.message || 'Some test runs could not be deleted';
        setError(failed.length === targets.length ? msg : `${failed.length} of ${targets.length} deletes failed`);
      }
    } finally {
      setDeleteBusy(false);
    }
  }, [clearRunFromLocalState, fetchRuns]);

  const handleDeleteSelected = () => handleDeleteBulk(bulkSelection);

  const handleDeleteAllRuns = () => handleDeleteBulk(runs.map((run) => run.id));

  const handleRetry = async (id) => {
    if (singleRerunTestId) return;
    setRetryLoading(true);
    setError(null);
    try {
      const run = await api.retryIperfTestRun(id);
      setDetailRun(run);
      setSelectedRun(run.id);
      await fetchRuns();
    } catch (e) {
      setError(e.message || 'Failed to retry');
    } finally {
      setRetryLoading(false);
    }
  };

  const handleRerunSingleTest = async (testId) => {
    if (!detailRun?.id || !testId || singleRerunTestId || retryLoading || !isTerminal(detailRun.status)) return;
    setSingleRerunTestId(testId);
    setError(null);
    try {
      const run = await api.retryIperfSingleTest(detailRun.id, testId);
      setDetailRun(run);
      selectedRunRef.current = run.id;
      setSelectedRun(run.id);
      setSseKey(k => k + 1); // force SSE reconnect — run ID unchanged but status is now "running"
      await fetchRuns();
    } catch (e) {
      setError(e.message || 'Failed to re-run test');
    } finally {
      setSingleRerunTestId(null);
    }
  };

  const handleSelectRun = async (id) => {
    setSelectedRun(id);
    selectedRunRef.current = id;
    setShowDetail(false);
    setExpandedPaths(new Set());
    try {
      const data = await api.getIperfTestRun(id);
      // Discard if the user switched to a different run while this fetch was in flight
      if (id === selectedRunRef.current) setDetailRun(data);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleCompareSelected = async () => {
    if (compareSelection.length !== 2 || compareLoading) return;
    const requestedIDs = [...compareSelection];
    setCompareLoading(true);
    setError(null);
    try {
      const details = await Promise.all(requestedIDs.map(id => api.getIperfTestRun(id)));
      const selectionStillCurrent = requestedIDs.length === compareSelectionRef.current.length &&
        requestedIDs.every((id, idx) => id === compareSelectionRef.current[idx]);
      if (!selectionStillCurrent) return;
      if (!details.every(run => run?.status === 'completed')) {
        setCompareRuns([]);
        setCompareSelection(details.filter(run => run?.status === 'completed').map(run => run.id));
        throw new Error('Only completed iperf runs can be compared');
      }
      // Newer row is usually selected first by the operator; compare chronologically.
      details.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
      setCompareRuns(details);
    } catch (e) {
      setError(e.message || 'Failed to compare iperf test runs');
    } finally {
      setCompareLoading(false);
    }
  };

  const handleClearCompare = () => {
    setCompareSelection([]);
    setCompareRuns([]);
  };

  const togglePath = (key) => {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const progress = detailRun
    ? ((detailRun.summary?.completed || 0) + (detailRun.summary?.failed || 0) +
       (detailRun.summary?.timed_out || 0) + (detailRun.summary?.cancelled || 0)) /
      Math.max(detailRun.summary?.total_tests || 1, 1) * 100
    : 0;

  const capSections = useMemo(
    () => (detailRun ? resolveCapSections(detailRun, deviceLookup) : []),
    [detailRun, deviceLookup],
  );
  const coverageLabels = useMemo(
    () => getIperfCoverageLabels(reverseDirection),
    [reverseDirection],
  );

  const hasFailedTests = detailRun?.tests?.some(t => t.status === 'failed' || t.status === 'timed_out');
  const canRerunIndividualTests = detailRun && isTerminal(detailRun.status);
  /** While a POST is in-flight, disable all per-test buttons */
  const singleRerunGloballyBusy = singleRerunTestId != null || retryLoading;

  return (
    <Box sx={{ p: 2 }}>
      {/* Start controls */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <SpeedIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>Tree-Wide Iperf Test</Typography>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>CAP Devices</InputLabel>
            <Select
              multiple
              value={selectedCapSerials}
              onChange={handleCapSelectionChange}
              label="CAP Devices"
              renderValue={() => capSelectionLabel}
            >
              {caps.length > 1 && (
                <MenuItem value="__all__">
                  <Checkbox
                    checked={selectedCapSerials.length === caps.length && caps.length > 0}
                    indeterminate={
                      selectedCapSerials.length > 0 && selectedCapSerials.length < caps.length
                    }
                  />
                  <ListItemText primary={`All CAPs (${caps.length})`} />
                </MenuItem>
              )}
              {caps.map((d) => (
                <MenuItem key={d.serial_number} value={d.serial_number}>
                  <Checkbox checked={selectedCapSerials.indexOf(d.serial_number) > -1} />
                  <ListItemText
                    primary={d.serial_number}
                    secondary={d.name && d.name !== d.serial_number ? d.name : undefined}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={startLoading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            onClick={handleStart}
            disabled={selectedCapSerials.length === 0 || startLoading}
          >
            Start Test
          </Button>
          <Button size="small" onClick={() => setShowParams(!showParams)}>
            {showParams ? 'Hide' : 'Show'} Parameters
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Each selected CAP starts its own test run. Multiple CAPs run one after another.
        </Typography>
        {mapFocusSerials.length > 0 && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Map selection: iperf is limited to {mapFocusSerials.length} selected device(s) and their uplink paths under the chosen CAP(s).
          </Alert>
        )}
        <Collapse in={showParams}>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              size="small"
              label="Test Name"
              value={testName}
              onChange={e => setTestName(e.target.value)}
              placeholder={defaultIperfRunName(selectedCapSerials, caps) || 'Optional run name'}
              sx={{ maxWidth: 420 }}
            />
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                onClick={() => setShowTimingParams((v) => !v)}
                sx={{ cursor: 'pointer', userSelect: 'none', mb: showTimingParams ? 1 : 0 }}
              >
                <IconButton size="small" tabIndex={-1}>
                  {showTimingParams
                    ? <KeyboardArrowDownIcon fontSize="small" />
                    : <KeyboardArrowRightIcon fontSize="small" />}
                </IconButton>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Run timing &amp; bandwidth
                </Typography>
                {!showTimingParams && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    — {duration}s · {protocol}
                    {protocol === 'UDP' ? ` · ${udpBandwidth} kbps` : ''}
                  </Typography>
                )}
              </Stack>
              <Collapse in={showTimingParams}>
                <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ pl: 4.5, pt: 0.5 }}>
                  <TextField
                    size="small"
                    label="Duration (s)"
                    type="number"
                    value={duration}
                    onChange={e => setDuration(Number(e.target.value))}
                    sx={{ width: 120 }}
                  />
                  <FormControl size="small" sx={{ width: 100 }}>
                    <InputLabel>Protocol</InputLabel>
                    <Select value={protocol} onChange={e => setProtocol(e.target.value)} label="Protocol">
                      <MenuItem value="UDP">UDP</MenuItem>
                      <MenuItem value="TCP">TCP</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="UDP BW (kbps)"
                    type="number"
                    value={udpBandwidth}
                    onChange={e => setUdpBandwidth(Number(e.target.value))}
                    disabled={protocol !== 'UDP'}
                    sx={{ width: 150 }}
                  />
                </Stack>
              </Collapse>
            </Box>
            <Divider />
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                Traffic direction
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                fullWidth
                value={reverseDirection ? 'downlink' : 'uplink'}
                onChange={(_, value) => {
                  if (value) setReverseDirection(value === 'downlink');
                }}
              >
                <ToggleButton value="uplink" sx={{ textTransform: 'none', py: 0.75 }}>
                  <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.75 }} />
                  Toward CAP
                </ToggleButton>
                <ToggleButton value="downlink" sx={{ textTransform: 'none', py: 0.75 }}>
                  <ArrowDownwardIcon fontSize="small" sx={{ mr: 0.75 }} />
                  From CAP
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {reverseDirection
                  ? 'Parent → child, CAP → leaf, CAP → every device (downlink / server on devices).'
                  : 'Child → parent, leaf → CAP, every device → CAP (default uplink).'}
              </Typography>
            </Paper>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                What to measure
              </Typography>
              <Stack spacing={1}>
                {[
                  {
                    key: 'hop',
                    checked: includeHopTests,
                    onChange: setIncludeHopTests,
                    meta: coverageLabels.hop,
                  },
                  {
                    key: 'e2e',
                    checked: includeE2ETests,
                    onChange: setIncludeE2ETests,
                    meta: coverageLabels.e2e,
                  },
                  {
                    key: 'allToCap',
                    checked: includeAllToCapTests,
                    onChange: setIncludeAllToCapTests,
                    meta: coverageLabels.allToCap,
                  },
                ].map(({ key, checked, onChange, meta }) => (
                  <Box
                    key={key}
                    onClick={() => onChange(!checked)}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      p: 1.25,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: checked ? 'primary.main' : 'divider',
                      bgcolor: checked ? 'primary.50' : 'background.paper',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s ease, background-color 0.15s ease',
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={(e) => {
                        e.stopPropagation();
                        onChange(e.target.checked);
                      }}
                      sx={{ pt: 0.25 }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{meta.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{meta.subtitle}</Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
              {/* Warn when selection won't feed optimize */}
              {!includeHopTests && !includeE2ETests && includeAllToCapTests && (
                <Alert severity="info" variant="outlined" sx={{ py: 0.5, mt: 1 }}>
                  <strong>Note:</strong> Only All-to-CAP tests are selected. This run will <strong>not</strong> be eligible as an iperf anchor for Optimize — the optimizer requires both Hop and End-to-End tests.
                </Alert>
              )}
            </Box>
          </Stack>
        </Collapse>
      </Paper>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {queuedInfo && <Alert severity="info" onClose={() => setQueuedInfo(null)} sx={{ mb: 2 }}>{queuedInfo}</Alert>}

      {/* Test runs list */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1}
          sx={{ p: 1.5 }}
        >
          <Typography variant="subtitle2">Test Runs</Typography>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant={compareMode ? 'contained' : 'outlined'}
              onClick={handleCompareModeToggle}
              disabled={deleteBusy || loading}
            >
              {compareMode ? 'Exit compare' : 'Compare mode'}
            </Button>
            {!compareMode && (
              <>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  disabled={bulkSelection.length === 0 || deleteBusy || loading}
                  onClick={handleDeleteSelected}
                >
                  Delete selected ({bulkSelection.length})
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  disabled={runs.length === 0 || deleteBusy || loading}
                  onClick={handleDeleteAllRuns}
                >
                  Delete all
                </Button>
              </>
            )}
            <IconButton size="small" onClick={fetchRuns} disabled={deleteBusy}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Tooltip title={
                    compareMode
                      ? 'Select up to two completed runs to compare'
                      : 'Select runs to delete'
                  }
                  >
                    <span>
                      <Checkbox
                        size="small"
                        checked={
                          compareMode
                            ? compareSelection.length === runs.filter((r) => r.status === 'completed').length
                              && runs.filter((r) => r.status === 'completed').length > 0
                            : runs.length > 0 && bulkSelection.length === runs.length
                        }
                        indeterminate={
                          compareMode
                            ? compareSelection.length > 0 && compareSelection.length < 2
                            : bulkSelection.length > 0 && bulkSelection.length < runs.length
                        }
                        disabled={runs.length === 0 || deleteBusy || compareMode}
                        onChange={handleToggleAllRows}
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>CAP</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Analysis</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">No test runs yet</Typography>
                  </TableCell>
                </TableRow>
              )}
              {runs.map(run => {
                const meta = STATUS_META[run.status] || STATUS_META.pending;
                const prog = run.summary?.total_tests > 0
                  ? `${(run.summary?.completed || 0)}/${run.summary?.total_tests}`
                  : '—';
                const selectedForCompare = compareSelection.includes(run.id);
                const canCompareRun = run.status === 'completed';
                const compareDisabled = compareLoading || !canCompareRun || (!selectedForCompare && compareSelection.length >= 2);
                return (
                  <TableRow
                    key={run.id}
                    hover
                    selected={selectedRun === run.id}
                    onClick={() => handleSelectRun(run.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={
                        compareMode
                          ? (canCompareRun
                            ? (compareDisabled ? 'Clear a selection first (max 2)' : 'Select for comparison')
                            : 'Only completed runs can be compared')
                          : 'Select for deletion'
                      }
                      >
                        <span>
                          <Checkbox
                            size="small"
                            checked={compareMode ? selectedForCompare : bulkSelection.includes(run.id)}
                            disabled={
                              deleteBusy
                              || (compareMode && (compareDisabled || !canCompareRun))
                            }
                            onChange={() => handleToggleRowSelect(run)}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 240 }}>
                        {runDisplayName(run, deviceLookup)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {runCapLabel(run, deviceLookup)}
                    </TableCell>
                    <TableCell><Chip size="small" label={meta.label} color={meta.color} /></TableCell>
                    <TableCell>{prog}</TableCell>
                    <TableCell>
                      {run.summary?.healthy_paths > 0 && (
                        <Chip size="small" label={`${run.summary.healthy_paths} healthy`} color="success" variant="outlined" sx={{ mr: 0.5 }} />
                      )}
                      {run.summary?.degraded_paths > 0 && (
                        <Chip size="small" label={`${run.summary.degraded_paths} degraded`} color="error" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>{relativeTime(run.created_at)}</TableCell>
                    <TableCell align="right">
                      {run.status === 'running' && (
                        <Tooltip title="Cancel active test">
                          <IconButton size="small" onClick={e => { e.stopPropagation(); handleCancel(run.id); }} disabled={deleteBusy}>
                            <StopIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={run.status === 'running'
                        ? 'Remove stuck run (not active on server)'
                        : 'Delete from history'}
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={deleteBusy}
                            onClick={e => { e.stopPropagation(); handleDelete(run.id); }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: 'divider' }}
        >
          <Typography variant="caption" color="text.secondary">
            {compareMode
              ? `Select up to two completed runs, then compare. Shows throughput deltas ≥${DIFF_THRESHOLD_PCT}% and loss deltas ≥${DIFF_THRESHOLD_PCT}% or ≥${LOSS_THRESHOLD_PP} pp.`
              : 'Turn on Compare mode to select runs for side-by-side comparison.'}
          </Typography>
          {compareMode && (
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {compareSelection.length > 0 && (
                <Button size="small" onClick={handleClearCompare}>
                  Clear
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                disabled={compareSelection.length !== 2 || compareLoading}
                onClick={handleCompareSelected}
                startIcon={compareLoading ? <CircularProgress size={14} /> : null}
              >
                Compare selected ({compareSelection.length}/2)
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>

      {comparison && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ p: 1.5 }}>
            <Box>
              <Typography variant="subtitle2">Compare Iperf Runs</Typography>
              <Typography variant="caption" color="text.secondary">
                Baseline: {runLabel(comparison.beforeRun, deviceLookup)} · Compare: {runLabel(comparison.afterRun, deviceLookup)}
              </Typography>
            </Box>
            <Button size="small" onClick={handleClearCompare}>Close</Button>
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ px: 1.5, pb: 1.5, gap: 0.75 }}>
            <Chip size="small" label={`${comparison.compared} matched tests`} />
            <Chip
              size="small"
              color={comparison.throughputImproved > 0 ? 'success' : 'default'}
              variant="outlined"
              label={`${comparison.throughputImproved} throughput better`}
            />
            <Chip
              size="small"
              color={comparison.throughputDegraded > 0 ? 'error' : 'default'}
              variant="outlined"
              label={`${comparison.throughputDegraded} throughput worse`}
            />
            <Chip
              size="small"
              color={comparison.lossImproved > 0 ? 'success' : 'default'}
              variant="outlined"
              label={`${comparison.lossImproved} loss better`}
            />
            <Chip
              size="small"
              color={comparison.lossDegraded > 0 ? 'error' : 'default'}
              variant="outlined"
              label={`${comparison.lossDegraded} loss worse`}
            />
            {(comparison.unmatchedBefore > 0 || comparison.unmatchedAfter > 0) && (
              <Tooltip title={`${comparison.unmatchedBefore} tests only in baseline, ${comparison.unmatchedAfter} tests only in compare run`}>
                <Chip size="small" color="warning" variant="outlined" label="Partial overlap" />
              </Tooltip>
            )}
          </Stack>
          {comparison.rows.length === 0 ? (
            <Alert severity="success" sx={{ mx: 1.5, mb: 1.5 }}>
              No matched hop or E2E test changed by more than the configured thresholds.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Path</TableCell>
                    <TableCell align="right">Throughput</TableCell>
                    <TableCell align="right">Loss</TableCell>
                    <TableCell>Verdict</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparison.rows.map(row => {
                    const verdictMeta = row.verdict === 'better'
                      ? { color: 'success', label: 'Better' }
                      : row.verdict === 'worse'
                        ? { color: 'error', label: 'Worse' }
                        : { color: 'warning', label: 'Mixed' };
                    return (
                      <TableRow key={row.key}>
                        <TableCell>
                          <Chip size="small" variant="outlined" label={compareTypeLabel(row.type)} />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.path}</TableCell>
                        <TableCell align="right">
                          {row.throughputSignificant ? (
                            <Stack spacing={0} alignItems="flex-end">
                              <Typography variant="body2" color={row.throughputDelta >= 0 ? 'success.main' : 'error.main'}>
                                {fmt(row.beforeMbps)} → {fmt(row.afterMbps)} Mbps
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {fmtSigned(row.throughputDelta)} Mbps ({fmtSigned(row.throughputPct)}%)
                              </Typography>
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {row.beforeMbps == null || row.afterMbps == null
                                ? '—'
                                : `${fmt(row.beforeMbps)} → ${fmt(row.afterMbps)} Mbps`}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {row.lossSignificant ? (
                            <Stack spacing={0} alignItems="flex-end">
                              <Typography variant="body2" color={row.lossDeltaPP <= 0 ? 'success.main' : 'error.main'}>
                                {fmt(row.beforeLoss)}% → {fmt(row.afterLoss)}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {fmtSigned(row.lossDeltaPP)} pp
                                {row.lossPct != null ? ` (${fmtSigned(row.lossPct)}%)` : ''}
                              </Typography>
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {row.beforeLoss == null || row.afterLoss == null
                                ? '—'
                                : `${fmt(row.beforeLoss)}% → ${fmt(row.afterLoss)}%`}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" color={verdictMeta.color} variant="outlined" label={verdictMeta.label} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Detail view */}
      {selectedRun && detailRun && (
        <Paper variant="outlined">
          {/* Clickable header — always visible, toggles content */}
          <Stack
            direction="row" justifyContent="space-between" alignItems="center"
            onClick={() => setShowDetail(v => !v)}
            sx={{ p: 2, cursor: 'pointer', userSelect: 'none' }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton size="small" tabIndex={-1}>
                {showDetail ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
              </IconButton>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Test Run — {runDisplayName(detailRun, deviceLookup)}
                </Typography>
                {detailRun.name && (
                  <Typography variant="caption" color="text.secondary">
                    CAP: {runCapLabel(detailRun, deviceLookup)}
                  </Typography>
                )}
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                variant="outlined"
                label={detailRun.reverse_direction ? 'From CAP' : 'Toward CAP'}
                color={detailRun.reverse_direction ? 'secondary' : 'default'}
              />
              {isTerminal(detailRun.status) && hasFailedTests && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={retryLoading ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
                  onClick={e => { e.stopPropagation(); handleRetry(detailRun.id); }}
                  disabled={retryLoading || !!singleRerunTestId}
                >
                  Retry Failed
                </Button>
              )}
              <Chip
                size="small"
                label={(STATUS_META[detailRun.status] || STATUS_META.pending).label}
                color={(STATUS_META[detailRun.status] || STATUS_META.pending).color}
              />
            </Stack>
          </Stack>

          <Collapse in={showDetail} timeout="auto" unmountOnExit>
          <Box sx={{ px: 2, pb: 2 }}>
          {!isTerminal(detailRun.status) && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {detailRun.summary?.completed || 0} / {detailRun.summary?.total_tests || 0} tests completed
              </Typography>
            </Box>
          )}

          {capSections.map((section) => {
            const sectionLabels = getIperfCoverageLabels(section.reverse);
            return (
            <Box key={section.capSerial} sx={{ mb: capSections.length > 1 ? 3 : 0 }}>
              {capSections.length > 1 && (
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ mb: 1.5, fontFamily: 'monospace' }}
                >
                  CAP {section.capSerial}
                </Typography>
              )}

          {/* Hop tests */}
          {section.hopTests.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Stack
                direction="row" alignItems="center" spacing={0.5}
                onClick={() => setShowHopTests(v => !v)}
                sx={{ cursor: 'pointer', mb: showHopTests ? 1 : 0, userSelect: 'none' }}
              >
                <IconButton size="small" tabIndex={-1}>
                  {showHopTests ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                </IconButton>
                <Typography variant="subtitle2">
                  Link tests — {sectionLabels.hop.title}
                </Typography>
              </Stack>
              <Collapse in={showHopTests} timeout="auto" unmountOnExit>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>From → To</TableCell>
                      <TableCell>Ch / Band</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Throughput</TableCell>
                      <TableCell align="right">Loss</TableCell>
                      <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{RF_STATS_TOOLTIP}</span>} placement="top">
                        <TableCell align="right" sx={{ cursor: 'help' }}>RF Stats</TableCell>
                      </Tooltip>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap', width: 72 }}>
                        Again
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {section.hopTests.map(t => {
                      const meta = STATUS_META[t.status] || STATUS_META.pending;
                      const chBand = resolveChannelBand(t, deviceLookup, section.reverse);
                      const rerunBusyRow = singleRerunTestId === t.id;
                      return (
                        <TableRow key={t.id}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {t.from_serial?.slice(-6)} → {t.to_serial?.slice(-6)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                            {chBand || '—'}
                          </TableCell>
                          <TableCell>
                            {t.status === 'running'
                              ? <CircularProgress size={14} />
                              : <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />
                            }
                          </TableCell>
                          <TableCell align="right">{t.status === 'completed' ? `${fmt(resolveThroughput(t))} Mbps` : '—'}</TableCell>
                          <TableCell align="right">{t.status === 'completed' ? (resolveLoss(t) != null ? `${fmt(resolveLoss(t))}%` : '—') : '—'}</TableCell>
                          {t.status === 'completed' ? <RfStatsCell test={t} /> : <TableCell align="right">—</TableCell>}
                          <TableCell align="center">
                            <Tooltip title={canRerunIndividualTests ? 'Re-run this hop test using the same run parameters' : 'Available when run is finished'}>
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={`Re-run hop ${t.from_serial} to ${t.to_serial}`}
                                  onClick={() => handleRerunSingleTest(t.id)}
                                  disabled={
                                    !canRerunIndividualTests
                                    || singleRerunGloballyBusy
                                  }
                                >
                                  {rerunBusyRow ? <CircularProgress size={14} /> : <ReplayIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              </Collapse>
            </Box>
          )}

          {section.allToCapTests.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Stack
                direction="row" alignItems="center" spacing={0.5}
                onClick={() => setShowAllToCapTests(v => !v)}
                sx={{ cursor: 'pointer', mb: showAllToCapTests ? 1 : 0, userSelect: 'none' }}
              >
                <IconButton size="small" tabIndex={-1}>
                  {showAllToCapTests ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                </IconButton>
                <Typography variant="subtitle2">
                  Direct to CAP — {sectionLabels.allToCap.title}
                </Typography>
              </Stack>
              <Collapse in={showAllToCapTests} timeout="auto" unmountOnExit>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>From → To</TableCell>
                      <TableCell>Ch / Band</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Throughput</TableCell>
                      <TableCell align="right">Loss</TableCell>
                      <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{RF_STATS_TOOLTIP}</span>} placement="top">
                        <TableCell align="right" sx={{ cursor: 'help' }}>RF Stats</TableCell>
                      </Tooltip>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap', width: 72 }}>
                        Again
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {section.allToCapTests.map(t => {
                      const meta = STATUS_META[t.status] || STATUS_META.pending;
                      const chBand = resolveChannelBand(t, deviceLookup, section.reverse);
                      const rerunBusyRow = singleRerunTestId === t.id;
                      return (
                        <TableRow key={t.id}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {t.from_serial?.slice(-6)} → {t.to_serial?.slice(-6)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                            {chBand || '—'}
                          </TableCell>
                          <TableCell>
                            {t.status === 'running'
                              ? <CircularProgress size={14} />
                              : <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />
                            }
                          </TableCell>
                          <TableCell align="right">{t.status === 'completed' ? `${fmt(resolveThroughput(t))} Mbps` : '—'}</TableCell>
                          <TableCell align="right">{t.status === 'completed' ? (resolveLoss(t) != null ? `${fmt(resolveLoss(t))}%` : '—') : '—'}</TableCell>
                          {t.status === 'completed' ? <RfStatsCell test={t} /> : <TableCell align="right">—</TableCell>}
                          <TableCell align="center">
                            <Tooltip title={canRerunIndividualTests ? 'Re-run this test using the same run parameters' : 'Available when run is finished'}>
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={`Re-run ${t.from_serial} to ${t.to_serial}`}
                                  onClick={() => handleRerunSingleTest(t.id)}
                                  disabled={!canRerunIndividualTests || singleRerunGloballyBusy}
                                >
                                  {rerunBusyRow ? <CircularProgress size={14} /> : <ReplayIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              </Collapse>
            </Box>
          )}

          {/* Path Analysis for End to End — merged E2E + analysis, expandable per-hop */}
          {section.e2eTests.length > 0 && (
            <Box>
              <Stack
                direction="row" alignItems="center" spacing={0.5}
                onClick={() => setShowPathAnalysis(v => !v)}
                sx={{ cursor: 'pointer', mb: showPathAnalysis ? 1 : 0, userSelect: 'none' }}
              >
                <IconButton size="small" tabIndex={-1}>
                  {showPathAnalysis ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                </IconButton>
                <Typography variant="subtitle2">
                  End-to-end — {sectionLabels.e2e.title}
                </Typography>
              </Stack>
              <Collapse in={showPathAnalysis} timeout="auto" unmountOnExit>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Path</TableCell>
                      <TableCell>Hops</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">E2E</TableCell>
                      <TableCell align="right">Loss</TableCell>
                      <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{RF_STATS_TOOLTIP}</span>} placement="top">
                        <TableCell align="right" sx={{ cursor: 'help' }}>RF Stats</TableCell>
                      </Tooltip>
                      <TableCell align="right">Bottleneck</TableCell>
                      <TableCell align="right">Expected</TableCell>
                      <TableCell align="right">Degradation</TableCell>
                      <TableCell>Health</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap', width: 72 }}>
                        Again
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {section.e2eTests.map((t, i) => {
                      const dp = buildPathForE2ETest(t, section.analysisPaths, detailRun.tests || []);
                      const expandKey = pathExpandKey(section.capSerial, i);
                      const expanded = expandedPaths.has(expandKey);
                      const eMeta = STATUS_META[t.status] || STATUS_META.pending;
                      const done = t.status === 'completed';
                      const hasAnalysis = typeof dp.bottleneck_hop_mbps === 'number' && dp.bottleneck_hop_mbps > 0;
                      const rerunBusyRow = singleRerunTestId === t.id;
                      return (
                        <React.Fragment key={t.id}>
                          <TableRow
                            hover
                            onClick={() => togglePath(expandKey)}
                            sx={{ cursor: 'pointer', '& > *': { borderBottom: expanded ? 'unset' : undefined } }}
                          >
                            <TableCell padding="checkbox">
                              <IconButton size="small">
                                {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {t.from_serial?.slice(-6)} → {t.to_serial?.slice(-6)}
                            </TableCell>
                            <TableCell>{t.path_hops || '—'}</TableCell>
                            <TableCell>
                              {t.status === 'running'
                                ? <CircularProgress size={14} />
                                : <Chip size="small" label={eMeta.label} color={eMeta.color} variant="outlined" />
                              }
                            </TableCell>
                            <TableCell align="right">{done ? `${fmt(resolveThroughput(t))} Mbps` : '—'}</TableCell>
                            <TableCell align="right">{done ? (resolveLoss(t) != null ? `${fmt(resolveLoss(t))}%` : '—') : '—'}</TableCell>
                            {done ? <RfStatsCell test={t} /> : <TableCell align="right">—</TableCell>}
                            <TableCell align="right">{hasAnalysis ? `${fmt(dp.bottleneck_hop_mbps)} Mbps` : '—'}</TableCell>
                            <TableCell align="right">{hasAnalysis ? `≥${fmt(dp.expected_e2e_mbps)} Mbps` : '—'}</TableCell>
                            <TableCell align="right">
                              {hasAnalysis && typeof dp.degradation_pct === 'number'
                                ? `${fmt(dp.degradation_pct)}%`
                                : 'None'}
                            </TableCell>
                            <TableCell>
                              {dp.healthy === true && (
                                <Chip size="small" icon={<CheckCircleIcon />} label="Healthy" color="success" variant="outlined" />
                              )}
                              {dp.healthy === false && hasAnalysis && (
                                <Chip size="small" icon={<WarningIcon />} label="Degraded" color="warning" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell align="center" onClick={e => e.stopPropagation()}>
                              <Tooltip title={canRerunIndividualTests ? 'Re-run this end-to-end test using the same run parameters' : 'Available when run is finished'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    aria-label={`Re-run E2E ${t.from_serial} to ${t.to_serial}`}
                                    onClick={() => handleRerunSingleTest(t.id)}
                                    disabled={!canRerunIndividualTests || singleRerunGloballyBusy}
                                  >
                                    {rerunBusyRow ? <CircularProgress size={14} /> : <ReplayIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={12} sx={{ py: 0, px: 0 }}>
                              <Collapse in={expanded} timeout="auto" unmountOnExit>
                                <Box sx={{ mx: 3, my: 1 }}>
                                  {dp.healthy === false && dp.flagged_hop && (
                                    <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
                                      Likely cause: {dp.flagged_hop}
                                    </Alert>
                                  )}
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Hop</TableCell>
                                        <TableCell>Ch / Band</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Throughput</TableCell>
                                        <TableCell align="right">Loss</TableCell>
                                        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{RF_STATS_TOOLTIP}</span>} placement="top">
                                          <TableCell align="right" sx={{ cursor: 'help' }}>RF Stats</TableCell>
                                        </Tooltip>
                                        <TableCell align="center" sx={{ width: 72 }}>
                                          Again
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {dp.hopChain.map((hop, hi) => {
                                        const meta = STATUS_META[hop.status] || STATUS_META.pending;
                                        const hopChBand = resolveChannelBand(hop, deviceLookup, section.reverse);
                                        const rerunHopBusy = hop.id && singleRerunTestId === hop.id;
                                        return (
                                          <TableRow key={hop.id || hi}>
                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                              {hop.from_serial?.slice(-6)} → {hop.to_serial?.slice(-6)}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                                              {hopChBand || '—'}
                                            </TableCell>
                                            <TableCell>
                                              <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />
                                            </TableCell>
                                            <TableCell align="right">
                                              {hop.status === 'completed' ? `${fmt(resolveThroughput(hop))} Mbps` : '—'}
                                            </TableCell>
                                            <TableCell align="right">
                                              {hop.status === 'completed' ? (resolveLoss(hop) != null ? `${fmt(resolveLoss(hop))}%` : '—') : '—'}
                                            </TableCell>
                                            {hop.status === 'completed' ? <RfStatsCell test={hop} /> : <TableCell align="right">—</TableCell>}
                                            <TableCell align="center">
                                              {hop.id ? (
                                                <Tooltip title={canRerunIndividualTests ? 'Re-run this hop using the same run parameters' : 'Available when run is finished'}>
                                                  <span>
                                                    <IconButton
                                                      size="small"
                                                      aria-label="Re-run hop"
                                                      onClick={() => handleRerunSingleTest(hop.id)}
                                                      disabled={!canRerunIndividualTests || singleRerunGloballyBusy}
                                                    >
                                                      {rerunHopBusy ? <CircularProgress size={14} /> : <ReplayIcon sx={{ fontSize: 16 }} />}
                                                    </IconButton>
                                                  </span>
                                                </Tooltip>
                                              ) : '—'}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              </Collapse>
            </Box>
          )}
            </Box>
          );
          })}
          </Box>
          </Collapse>
        </Paper>
      )}
    </Box>
  );
};

export default IperfTestPanel;
